import type {
  CreateEngineerBody,
  Engineer,
  UpdateEngineerBody,
} from '@lib/api-types/engineers';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, unwrap } from '../api/client.ts';

/**
 * View-model hooks for the engineers resource. Mirror of
 * view-models/business-functions.ts.
 */

export interface ListEngineersOptions {
  atEffective?: number;
  atAuthored?: number;
  branchId?: number;
  includeDeparted?: boolean;
}

const KEYS = {
  all: (slug: string) => ['engineers', slug] as const,
  list: (slug: string, opts: ListEngineersOptions) =>
    ['engineers', slug, 'list', opts] as const,
};

function listQuery(opts: ListEngineersOptions) {
  const q: Record<string, string> = {};
  if (opts.atEffective !== undefined) q.at_effective = String(opts.atEffective);
  if (opts.atAuthored !== undefined) q.at_authored = String(opts.atAuthored);
  if (opts.branchId !== undefined) q.branch_id = String(opts.branchId);
  if (opts.includeDeparted !== undefined) q.include_departed = String(opts.includeDeparted);
  return q;
}

interface ListResponseBody {
  object: 'list';
  url: string;
  has_more: boolean;
  data: Engineer[];
}

interface SingleResponseBody {
  engineer: Engineer;
}

interface DeleteResponseBody {
  deleted: true;
  id: string;
}

export function useEngineers(orgSlug: string | null, opts: ListEngineersOptions = {}) {
  return useQuery({
    queryKey: orgSlug
      ? KEYS.list(orgSlug, opts)
      : ['engineers', null, 'list'],
    queryFn: async () => {
      if (!orgSlug) throw new Error('useEngineers called without orgSlug');
      const res = await apiClient.v1.orgs[':slug'].engineers.$get({
        param: { slug: orgSlug },
        query: listQuery(opts),
      });
      const body = await unwrap<ListResponseBody>(res);
      return body.data;
    },
    enabled: !!orgSlug,
    staleTime: 30_000,
  });
}

export function useEngineer(orgSlug: string | null, id: string | null) {
  return useQuery({
    queryKey: orgSlug && id ? (['engineers', orgSlug, 'one', id] as const) : (['engineers', null, 'one'] as const),
    queryFn: async () => {
      if (!orgSlug || !id) throw new Error('useEngineer called without orgSlug+id');
      const res = await apiClient.v1.orgs[':slug'].engineers[':id'].$get({
        param: { slug: orgSlug, id },
        query: {},
      });
      const body = await unwrap<SingleResponseBody>(res);
      return body.engineer;
    },
    enabled: !!orgSlug && !!id,
    staleTime: 30_000,
  });
}

export function useCreateEngineer(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateEngineerBody) => {
      const res = await apiClient.v1.orgs[':slug'].engineers.$post({
        param: { slug: orgSlug },
        json: body,
      });
      const data = await unwrap<SingleResponseBody>(res);
      return data.engineer;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all(orgSlug) });
    },
  });
}

export function useUpdateEngineer(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; body: UpdateEngineerBody }) => {
      const res = await apiClient.v1.orgs[':slug'].engineers[':id'].$patch({
        param: { slug: orgSlug, id: args.id },
        json: args.body,
      });
      const data = await unwrap<SingleResponseBody>(res);
      return data.engineer;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all(orgSlug) });
    },
  });
}

export function useDepartEngineer(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; effectiveAt?: number }) => {
      const res = await apiClient.v1.orgs[':slug'].engineers[':id'].$patch({
        param: { slug: orgSlug, id: args.id },
        json: {
          status: 'departed' as const,
          ...(args.effectiveAt !== undefined ? { effective_at: args.effectiveAt } : {}),
        },
      });
      const data = await unwrap<SingleResponseBody>(res);
      return data.engineer;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all(orgSlug) });
    },
  });
}

/** Bring a departed engineer back — inverse of {@link useDepartEngineer}. */
export function useRestoreEngineer(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; effectiveAt?: number }) => {
      const res = await apiClient.v1.orgs[':slug'].engineers[':id'].$patch({
        param: { slug: orgSlug, id: args.id },
        json: {
          status: 'active' as const,
          ...(args.effectiveAt !== undefined ? { effective_at: args.effectiveAt } : {}),
        },
      });
      const data = await unwrap<SingleResponseBody>(res);
      return data.engineer;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all(orgSlug) });
    },
  });
}

export function useDeleteEngineer(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.v1.orgs[':slug'].engineers[':id'].$delete({
        param: { slug: orgSlug, id },
      });
      return unwrap<DeleteResponseBody>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all(orgSlug) });
    },
  });
}

/**
 * Manual identity merge — fold `loserIds` (duplicate engineers) into
 * `targetId`. The matrices change (an engineer disappears + the survivor
 * gains identities), so invalidate the views too.
 */
export function useMergeEngineers(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { targetId: string; loserIds: string[] }) => {
      const res = await apiClient.v1.orgs[':slug'].engineers[':id'].merge.$post({
        param: { slug: orgSlug, id: args.targetId },
        json: { loser_ids: args.loserIds },
      });
      const data = await unwrap<SingleResponseBody>(res);
      return data.engineer;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all(orgSlug) });
      void qc.invalidateQueries({ queryKey: ['view'] });
    },
  });
}
